/**
 * MagicBI Design System - Section Component
 * 
 * Flexible section component for page layout and content organization.
 */

import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';

// Section variant types
type SectionVariant = 'default' | 'hero' | 'feature' | 'cta';
type SectionSize = 'sm' | 'md' | 'lg' | 'xl';

// Section props interface
export interface SectionProps extends React.HTMLAttributes<HTMLElement> {
  /** Section variant */
  variant?: SectionVariant;
  /** Section size (padding) */
  size?: SectionSize;
  /** Custom background color */
  backgroundColor?: string;
  /** Whether section is full width */
  fullWidth?: boolean;
  /** Whether section is centered */
  centered?: boolean;
  /** Custom padding */
  padding?: string;
  /** Custom margin */
  margin?: string;
  /** Children elements */
  children: React.ReactNode;
}

// Get variant styles
const getVariantStyles = (variant: SectionVariant) => {
  switch (variant) {
    case 'hero':
      return css`
        background: ${({ theme }) => theme.colors.background.gradientPrimary};
        color: ${({ theme }) => theme.colors.text.primary};
        text-align: center;
        position: relative;
        overflow: hidden;
        
        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: ${({ theme }) => theme.colors.surface.overlay};
          opacity: 0.1;
          pointer-events: none;
        }
      `;
    
    case 'feature':
      return css`
        background-color: ${({ theme }) => theme.colors.surface.secondary};
      `;
    
    case 'cta':
      return css`
        background: ${({ theme }) => theme.colors.background.gradientPrimary};
        color: ${({ theme }) => theme.colors.text.inverse};
        text-align: center;
      `;
    
    case 'default':
    default:
      return css`
        background-color: ${({ theme }) => theme.colors.surface.primary};
      `;
  }
};

// Get size styles
const getSizeStyles = (size: SectionSize) => {
  switch (size) {
    case 'sm':
      return css`
        padding: ${({ theme }) => theme.spacing.layout.section.gap.sm} 0;
      `;
    
    case 'lg':
      return css`
        padding: ${({ theme }) => theme.spacing.layout.section.gap.lg} 0;
      `;
    
    case 'xl':
      return css`
        padding: ${({ theme }) => theme.spacing.layout.section.gap.lg} 0;
      `;
    
    case 'md':
    default:
      return css`
        padding: ${({ theme }) => theme.spacing.layout.section.gap.md} 0;
      `;
  }
};

// Styled section component
const StyledSection = styled.section<SectionProps>`
  width: 100%;
  position: relative;
  
  ${({ variant = 'default' }) => getVariantStyles(variant)}
  ${({ size = 'md' }) => getSizeStyles(size)}
  
  ${({ backgroundColor, theme }) => backgroundColor && css`
    background-color: ${backgroundColor.startsWith('#') ? backgroundColor : (theme.colors as any)[backgroundColor] || backgroundColor};
  `}
  
  ${({ padding }) => padding && css`
    padding: ${padding};
  `}
  
  ${({ margin }) => margin && css`
    margin: ${margin};
  `}
  
  ${({ centered }) => centered && css`
    text-align: center;
  `}
`;

// Section content wrapper
const SectionContent = styled.div<{ fullWidth?: boolean }>`
  ${({ fullWidth, theme }) => !fullWidth && css`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 ${theme.spacing.layout.container.paddingX.mobile};
    
    @media (min-width: ${theme.breakpoints.values.md}px) {
      padding: 0 ${theme.spacing.layout.container.paddingX.tablet};
    }
    
    @media (min-width: ${theme.breakpoints.values.lg}px) {
      padding: 0 ${theme.spacing.layout.container.paddingX.desktop};
    }
  `}
  
  ${({ fullWidth }) => fullWidth && css`
    width: 100%;
  `}
`;

// Section component
export const Section = forwardRef<HTMLElement, SectionProps>(
  ({ children, fullWidth = false, ...props }, ref) => {
    return (
      <StyledSection ref={ref} {...props}>
        <SectionContent fullWidth={fullWidth}>
          {children}
        </SectionContent>
      </StyledSection>
    );
  }
);

Section.displayName = 'Section';

export default Section;
